"use client";

import { useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

interface FormState {
  name: string;
  phone: string;
  comment: string;
  consent: boolean;
}

const EMPTY: FormState = { name: "", phone: "", comment: "", consent: false };

/**
 * Sticky-кнопка "Перезвоните мне" в правом нижнем углу + модалка с формой.
 * POST на /api/callback (см. apps/api/app/routers/callback.py).
 *
 * Используется во всех роутах через layout.tsx. На страницах /tg/app/* и /max/app/*
 * — не нужна (отдельные боты), там можно скрыть условием по pathname.
 */
export function CallbackWidget() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fid = useId();

  // Скрываем виджет в miniapp-роутах ботов.
  const hidden = pathname.startsWith("/tg/app") || pathname.startsWith("/max/app");

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (form.name.trim().length < 2) errs.name = "Введите имя";
    if (form.phone.replace(/\D/g, "").length < 7) errs.phone = "Введите телефон";
    if (!form.consent) errs.consent = "Подтвердите согласие";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          comment: form.comment.trim() || null,
          context_url: pathname,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Спасибо! Перезвоним в рабочее время.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error("Не удалось отправить. Напишите на sales@сток.москва");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Sticky-кнопка снизу справа */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Заказать обратный звонок"
        className="fixed bottom-4 right-4 z-40 inline-flex h-14 items-center gap-2 rounded-full bg-accent-600 px-5 text-base font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:bg-accent-700 lg:bottom-6 lg:right-6"
        style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <span className="hidden sm:inline">Перезвоните мне</span>
      </button>

      <dialog
        ref={dialogRef}
        aria-label="Заказать обратный звонок"
        onClose={() => setOpen(false)}
        onClick={(e) => {
          if (e.target === dialogRef.current) setOpen(false);
        }}
        className="m-auto w-[min(420px,92vw)] rounded-[16px] bg-white p-0 backdrop:bg-black/40"
      >
        <form onSubmit={onSubmit} className="p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <h2 className="text-xl font-bold">Перезвоните мне</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Закрыть"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] hover:bg-bg-muted"
            >
              <svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
                <path d="m6 6 12 12M6 18 18 6" />
              </svg>
            </button>
          </div>
          <p className="mb-4 text-sm text-text-muted">
            Оставьте телефон — менеджер свяжется в&nbsp;рабочее время
            (Пн–Пт&nbsp;9:00–18:00).
          </p>

          <div className="space-y-3">
            <div>
              <label htmlFor={`${fid}-name`} className="mb-1 block text-sm font-medium">
                Имя *
              </label>
              <input
                id={`${fid}-name`}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={!!errors.name}
                autoComplete="name"
                className="flex h-11 w-full rounded-[8px] border border-border-strong bg-white px-3 text-base focus-visible:outline-2 focus-visible:outline-brand-500"
              />
              {errors.name && <p className="mt-1 text-xs text-error-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor={`${fid}-phone`} className="mb-1 block text-sm font-medium">
                Телефон *
              </label>
              <input
                id={`${fid}-phone`}
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                aria-invalid={!!errors.phone}
                placeholder="+7 (___) ___-__-__"
                autoComplete="tel"
                className="flex h-11 w-full rounded-[8px] border border-border-strong bg-white px-3 text-base focus-visible:outline-2 focus-visible:outline-brand-500"
              />
              {errors.phone && <p className="mt-1 text-xs text-error-600">{errors.phone}</p>}
            </div>
            <div>
              <label htmlFor={`${fid}-comment`} className="mb-1 block text-sm font-medium">
                Что вас интересует (необязательно)
              </label>
              <textarea
                id={`${fid}-comment`}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={3}
                className="flex w-full rounded-[8px] border border-border-strong bg-white px-3 py-2 text-base focus-visible:outline-2 focus-visible:outline-brand-500"
              />
            </div>
            <div className="flex items-start gap-2 rounded-[8px] bg-bg-muted p-3">
              <input
                id={`${fid}-consent`}
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5"
              />
              <label htmlFor={`${fid}-consent`} className="text-xs text-text-muted">
                Я даю{" "}
                <Link href="/consent" className="text-brand-700 hover:underline">
                  согласие на обработку персональных данных
                </Link>{" "}
                и ознакомлен(а) с{" "}
                <Link href="/policy" className="text-brand-700 hover:underline">
                  политикой обработки ПДн
                </Link>
                .
              </label>
            </div>
            {errors.consent && <p className="text-xs text-error-600">{errors.consent}</p>}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-[8px] bg-accent-600 text-base font-semibold text-white hover:bg-accent-700 disabled:bg-text-subtle"
          >
            {busy ? "Отправка…" : "Жду звонка"}
          </button>
        </form>
      </dialog>
    </>
  );
}
